/**
 * Full-size preview of one attachment, with previous/next across the set.
 *
 * Images render in an `<img>`, PDFs in an `<iframe>`, both from a `blob:` URL the
 * signed-in user fetched. The iframe is what the `frame-src 'self' blob:` entry in
 * deploy/nginx-app.conf exists for; the server also stamps every attachment response
 * with `Content-Security-Policy: sandbox`, so whatever is inside cannot run. If a
 * browser will not frame the document, the download button is always there.
 */
import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { TicketAttachment } from '@/api/support'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { formatBytes } from '@/lib/format'
import { downloadFile } from '@/lib/download'

import { useBlobUrl } from './useBlobUrl'

export function AttachmentPreview({
  attachments,
  index,
  onIndexChange,
  onClose,
  filePath,
}: {
  attachments: TicketAttachment[]
  /** Null closes the preview. */
  index: number | null
  onIndexChange: (index: number) => void
  onClose: () => void
  filePath: (attachment: TicketAttachment, inline: boolean) => string
}) {
  const { t } = useTranslation()
  const current = index === null ? null : (attachments[index] ?? null)
  const blob = useBlobUrl(current ? filePath(current, true) : null)
  // Zoom is remembered per index, so moving to the next file starts unzoomed
  // without an effect resetting state behind the render.
  const [zoomedAt, setZoomedAt] = useState<number | null>(null)
  const zoomed = zoomedAt !== null && zoomedAt === index
  const setZoomed = (value: boolean) => setZoomedAt(value ? index : null)

  const many = attachments.length > 1
  const previous = () =>
    index !== null && onIndexChange((index - 1 + attachments.length) % attachments.length)
  const next = () => index !== null && onIndexChange((index + 1) % attachments.length)

  useEffect(() => {
    if (index === null || !many) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') previous()
      if (event.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  return (
    <Dialog open={current !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex max-h-[92dvh] w-[calc(100vw-1rem)] max-w-5xl flex-col gap-3 p-3 sm:p-4">
        {current && (
          <>
            <div className="flex min-w-0 items-center gap-2 pe-8">
              <DialogTitle className="min-w-0 flex-1 truncate text-sm font-medium">
                {current.filename}
              </DialogTitle>
              <DialogDescription className="shrink-0 text-xs">
                {formatBytes(current.byte_size)}
                {many && index !== null && (
                  <>
                    {' · '}
                    {t('support.attachments.position', {
                      current: index + 1,
                      total: attachments.length,
                    })}
                  </>
                )}
              </DialogDescription>
            </div>

            <div className="relative flex min-h-[40dvh] flex-1 items-center justify-center overflow-auto rounded-md bg-surface-raised">
              {blob.status === 'loading' && (
                <span className="text-sm text-text-muted">{t('common.loading')}</span>
              )}
              {blob.status === 'error' && (
                <span className="text-sm text-text-muted">
                  {t('support.attachments.preview_failed')}
                </span>
              )}
              {blob.status === 'ready' && current.media_type.startsWith('image/') && (
                <button
                  type="button"
                  className="flex h-full w-full cursor-zoom-in items-center justify-center"
                  onClick={() => setZoomed(!zoomed)}
                  aria-label={
                    zoomed ? t('support.attachments.zoom_out') : t('support.attachments.zoom_in')
                  }
                >
                  <img
                    src={blob.url}
                    alt={current.filename}
                    className={zoomed ? 'max-w-none' : 'max-h-[70dvh] max-w-full object-contain'}
                  />
                </button>
              )}
              {blob.status === 'ready' && current.media_type === 'application/pdf' && (
                <iframe
                  src={blob.url}
                  // An iframe's title is its accessible name, which jsx-a11y requires;
                  // it is not the hover tooltip the project rule guards against.
                  // eslint-disable-next-line no-restricted-syntax
                  title={current.filename}
                  className="h-[70dvh] w-full rounded-md border-0 bg-surface"
                />
              )}

              {many && (
                <>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="absolute start-2 top-1/2 -translate-y-1/2"
                    aria-label={t('support.attachments.previous')}
                    onClick={previous}
                  >
                    <ChevronLeft size={18} aria-hidden />
                  </Button>
                  <Button
                    size="icon"
                    variant="secondary"
                    className="absolute end-2 top-1/2 -translate-y-1/2"
                    aria-label={t('support.attachments.next')}
                    onClick={next}
                  >
                    <ChevronRight size={18} aria-hidden />
                  </Button>
                </>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              {blob.status === 'ready' && (
                <Button asChild variant="secondary" size="sm">
                  <a href={blob.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink size={14} aria-hidden />
                    {t('support.attachments.open_tab')}
                  </a>
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => void downloadFile(filePath(current, false))}
              >
                <Download size={14} aria-hidden />
                {t('action.download')}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
